import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, 
  DollarSign, 
  Star, 
  LogOut, 
  Plus, 
  Pencil, 
  Trash2,
  Eye,
  Lock,
  Upload,
  Loader2,
  Menu,
  X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Login Component
const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem("adminAuth", btoa(password));
        onLogin(password);
        toast.success("Login erfolgreich!");
      }
    } catch (error) {
      toast.error("Ungültiges Passwort");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-yoga-cream flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Lock className="mx-auto mb-4 text-yoga-brown" size={48} strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-yoga-brown mb-2">Admin Login</h1>
          <p className="font-sans text-yoga-brown/60">DanyKizayYoga Verwaltung</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl p-8" data-testid="admin-login-form">
          <div className="mb-6">
            <Input
              type="password"
              placeholder="Passwort"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-transparent border-yoga-sand focus:border-yoga-brown"
              data-testid="admin-password"
            />
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-yoga-brown text-yoga-cream hover:bg-yoga-brown/90"
            data-testid="admin-login-submit"
          >
            {isLoading ? "Anmeldung..." : "Anmelden"}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ authPassword, onLogout }) => {
  const [activeTab, setActiveTab] = useState("schedule");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const authHeader = {
    auth: {
      username: "admin",
      password: authPassword,
    },
  };

  const tabs = [
    { id: "schedule", label: "Wochenplan", icon: Calendar },
    { id: "prices", label: "Preise", icon: DollarSign },
    { id: "events", label: "Events", icon: Star },
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-yoga-cream">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-yoga-brown z-50 px-4 py-3 flex items-center justify-between">
        <h1 className="font-serif text-lg text-yoga-cream">DanyKizayYoga</h1>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-yoga-cream p-2"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-yoga-brown p-6 flex flex-col z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        <div className="mb-8 hidden md:block">
          <h1 className="font-serif text-xl text-yoga-cream">DanyKizayYoga</h1>
          <p className="font-sans text-sm text-yoga-cream/60">Admin Dashboard</p>
        </div>

        <div className="mb-8 md:hidden pt-12">
          <p className="font-sans text-sm text-yoga-cream/60">Admin Dashboard</p>
        </div>

        <nav className="flex-1 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-sans text-sm transition-colors ${
                activeTab === tab.id
                  ? "bg-yoga-cream/20 text-yoga-cream"
                  : "text-yoga-cream/60 hover:bg-yoga-cream/10 hover:text-yoga-cream"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} strokeWidth={1.5} />
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="space-y-2">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="w-full justify-start text-yoga-cream/60 hover:text-yoga-cream hover:bg-yoga-cream/10"
          >
            <Eye size={18} strokeWidth={1.5} className="mr-3" />
            Website anzeigen
          </Button>
          <Button
            variant="ghost"
            onClick={onLogout}
            className="w-full justify-start text-yoga-cream/60 hover:text-yoga-cream hover:bg-yoga-cream/10"
            data-testid="admin-logout"
          >
            <LogOut size={18} strokeWidth={1.5} className="mr-3" />
            Abmelden
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pt-16 md:pt-0 md:ml-64 p-4 md:p-8">
        {activeTab === "schedule" && <ScheduleManager authHeader={authHeader} />}
        {activeTab === "prices" && <PricesManager authHeader={authHeader} />}
        {activeTab === "events" && <EventsManager authHeader={authHeader} />}
      </main>
    </div>
  );
};

// Schedule Manager
const ScheduleManager = ({ authHeader }) => {
  const [schedule, setSchedule] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    day: "Mo",
    time: "",
    title: "",
    duration: "1 Std.",
    location: "",
  });

  const days = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/schedule`);
      setSchedule(response.data);
    } catch (error) {
      console.error("Error loading schedule:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await axios.put(`${API}/admin/schedule/${editingEntry.id}`, formData, authHeader);
        toast.success("Eintrag aktualisiert");
      } else {
        await axios.post(`${API}/admin/schedule`, formData, authHeader);
        toast.success("Eintrag hinzugefügt");
      }
      fetchSchedule();
      resetForm();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Eintrag wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/schedule/${id}`, authHeader);
        toast.success("Eintrag gelöscht");
        fetchSchedule();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      day: entry.day,
      time: entry.time,
      title: entry.title,
      duration: entry.duration,
      location: entry.location || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      day: "Mo",
      time: "",
      title: "",
      duration: "1 Std.",
      location: "",
    });
    setIsDialogOpen(false);
  };

  return (
    <div data-testid="schedule-manager">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
        <h2 className="font-serif text-2xl md:text-3xl text-yoga-brown">Wochenplan verwalten</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => resetForm()}
              className="bg-yoga-brown text-yoga-cream hover:bg-yoga-brown/90 w-full sm:w-auto"
              data-testid="add-schedule-entry"
            >
              <Plus size={18} className="mr-2" /> Neuer Eintrag
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-yoga-cream max-w-[95vw] sm:max-w-lg mx-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl md:text-2xl text-yoga-brown">
                {editingEntry ? "Eintrag bearbeiten" : "Neuer Eintrag"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-yoga-brown">Tag</Label>
                  <select
                    value={formData.day}
                    onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-yoga-sand rounded-md bg-white text-sm"
                  >
                    {days.map((day) => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-yoga-brown">Uhrzeit</Label>
                  <Input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="mt-1 border-yoga-sand"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-yoga-brown">Kursname</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1 border-yoga-sand"
                  required
                />
              </div>
              <div>
                <Label className="text-yoga-brown">Dauer</Label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="mt-1 border-yoga-sand"
                  required
                />
              </div>
              <div>
                <Label className="text-yoga-brown">Ort (optional)</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="mt-1 border-yoga-sand"
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="w-full sm:w-auto">
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-yoga-brown text-yoga-cream hover:bg-yoga-brown/90 w-full sm:w-auto">
                  {editingEntry ? "Speichern" : "Hinzufügen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {schedule.map((entry) => (
          <div key={entry.id} className="bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-yoga-brown">{entry.day}</span>
                  <span className="text-yoga-brown/60">{entry.time}</span>
                </div>
                <div className="font-medium text-yoga-brown">{entry.title}</div>
                <div className="text-sm text-yoga-brown/60">{entry.duration}</div>
                {entry.location && <div className="text-sm text-yoga-brown/50">{entry.location}</div>}
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(entry)}
                  className="text-yoga-brown hover:bg-yoga-sand/50 p-2"
                >
                  <Pencil size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(entry.id)}
                  className="text-red-500 hover:bg-red-50 p-2"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-yoga-sand">
              <TableHead className="text-yoga-brown">Tag</TableHead>
              <TableHead className="text-yoga-brown">Uhrzeit</TableHead>
              <TableHead className="text-yoga-brown">Kurs</TableHead>
              <TableHead className="text-yoga-brown">Dauer</TableHead>
              <TableHead className="text-yoga-brown">Ort</TableHead>
              <TableHead className="text-yoga-brown text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedule.map((entry) => (
              <TableRow key={entry.id} className="border-yoga-sand">
                <TableCell className="font-medium">{entry.day}</TableCell>
                <TableCell>{entry.time}</TableCell>
                <TableCell>{entry.title}</TableCell>
                <TableCell>{entry.duration}</TableCell>
                <TableCell>{entry.location || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(entry)}
                    className="text-yoga-brown hover:bg-yoga-sand/50"
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(entry.id)}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Prices Manager
const PricesManager = ({ authHeader }) => {
  const [prices, setPrices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    price: "",
    description: "",
    validity: "",
    is_membership: false,
    sort_order: 0,
  });

  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${API}/prices`);
      setPrices(response.data);
    } catch (error) {
      console.error("Error loading prices:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPrice) {
        await axios.put(`${API}/admin/prices/${editingPrice.id}`, formData, authHeader);
        toast.success("Preis aktualisiert");
      } else {
        await axios.post(`${API}/admin/prices`, formData, authHeader);
        toast.success("Preis hinzugefügt");
      }
      fetchPrices();
      resetForm();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Preis wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/prices/${id}`, authHeader);
        toast.success("Preis gelöscht");
        fetchPrices();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (price) => {
    setEditingPrice(price);
    setFormData({
      title: price.title,
      price: price.price,
      description: price.description || "",
      validity: price.validity || "",
      is_membership: price.is_membership,
      sort_order: price.sort_order,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPrice(null);
    setFormData({
      title: "",
      price: "",
      description: "",
      validity: "",
      is_membership: false,
      sort_order: 0,
    });
    setIsDialogOpen(false);
  };

  return (
    <div data-testid="prices-manager">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
        <h2 className="font-serif text-2xl md:text-3xl text-yoga-brown">Preise verwalten</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => resetForm()}
              className="bg-yoga-brown text-yoga-cream hover:bg-yoga-brown/90 w-full sm:w-auto"
              data-testid="add-price"
            >
              <Plus size={18} className="mr-2" /> Neuer Preis
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-yoga-cream max-w-[95vw] sm:max-w-lg mx-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl md:text-2xl text-yoga-brown">
                {editingPrice ? "Preis bearbeiten" : "Neuer Preis"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-yoga-brown">Titel</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1 border-yoga-sand"
                  required
                />
              </div>
              <div>
                <Label className="text-yoga-brown">Preis</Label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="z.B. 65 €"
                  className="mt-1 border-yoga-sand"
                  required
                />
              </div>
              <div>
                <Label className="text-yoga-brown">Beschreibung (optional)</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 border-yoga-sand"
                />
              </div>
              <div>
                <Label className="text-yoga-brown">Gültigkeit (optional)</Label>
                <Input
                  value={formData.validity}
                  onChange={(e) => setFormData({ ...formData, validity: e.target.value })}
                  placeholder="z.B. 2 Monate gültig"
                  className="mt-1 border-yoga-sand"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_membership"
                  checked={formData.is_membership}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_membership: checked })}
                />
                <Label htmlFor="is_membership" className="text-yoga-brown">Mitgliedschaft</Label>
              </div>
              <div>
                <Label className="text-yoga-brown">Sortierung</Label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="mt-1 border-yoga-sand"
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="w-full sm:w-auto">
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-yoga-brown text-yoga-cream hover:bg-yoga-brown/90 w-full sm:w-auto">
                  {editingPrice ? "Speichern" : "Hinzufügen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {prices.map((price) => (
          <div key={price.id} className="bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-medium text-yoga-brown">{price.title}</div>
                <div className="text-lg font-semibold text-yoga-brown">{price.price}</div>
                {price.description && <div className="text-sm text-yoga-brown/60">{price.description}</div>}
                {price.validity && <div className="text-sm text-yoga-brown/50">{price.validity}</div>}
                <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${price.is_membership ? "bg-yoga-sage/20 text-yoga-brown" : "bg-gray-200 text-gray-600"}`}>
                  {price.is_membership ? "Mitgliedschaft" : "Einzelpreis"}
                </span>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(price)}
                  className="text-yoga-brown hover:bg-yoga-sand/50 p-2"
                >
                  <Pencil size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(price.id)}
                  className="text-red-500 hover:bg-red-50 p-2"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-yoga-sand">
              <TableHead className="text-yoga-brown">Titel</TableHead>
              <TableHead className="text-yoga-brown">Preis</TableHead>
              <TableHead className="text-yoga-brown">Beschreibung</TableHead>
              <TableHead className="text-yoga-brown">Gültigkeit</TableHead>
              <TableHead className="text-yoga-brown">Typ</TableHead>
              <TableHead className="text-yoga-brown text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prices.map((price) => (
              <TableRow key={price.id} className="border-yoga-sand">
                <TableCell className="font-medium">{price.title}</TableCell>
                <TableCell>{price.price}</TableCell>
                <TableCell>{price.description || "-"}</TableCell>
                <TableCell>{price.validity || "-"}</TableCell>
                <TableCell>{price.is_membership ? "Mitgliedschaft" : "Einzelpreis"}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(price)}
                    className="text-yoga-brown hover:bg-yoga-sand/50"
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(price.id)}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Events Manager
const EventsManager = ({ authHeader }) => {
  const [events, setEvents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    price: "",
    image_url: "",
    is_active: true,
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      // First try to get all events (admin view)
      const response = await axios.get(`${API}/admin/events`, authHeader);
      setEvents(response.data);
    } catch (error) {
      // Fallback to public events if admin fails
      try {
        const publicResponse = await axios.get(`${API}/events`);
        setEvents(publicResponse.data);
      } catch (publicError) {
        console.error("Error loading events:", publicError);
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Bitte nur Bilddateien hochladen");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Bild darf maximal 10MB groß sein");
      return;
    }

    setIsUploading(true);

    try {
      const sigResponse = await axios.get(`${API}/cloudinary/signature?folder=events`);
      const sig = sigResponse.data;

      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("api_key", sig.api_key);
      formDataUpload.append("timestamp", sig.timestamp);
      formDataUpload.append("signature", sig.signature);
      formDataUpload.append("folder", sig.folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: formDataUpload }
      );

      const uploadResult = await uploadResponse.json();

      if (uploadResult.secure_url) {
        setFormData({ ...formData, image_url: uploadResult.secure_url });
        toast.success("Bild erfolgreich hochgeladen!");
      } else {
        throw new Error("Upload fehlgeschlagen");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Fehler beim Hochladen des Bildes");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, formData, authHeader);
        toast.success("Event aktualisiert");
      } else {
        await axios.post(`${API}/admin/events`, formData, authHeader);
        toast.success("Event hinzugefügt");
      }
      fetchEvents();
      resetForm();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Fehler beim Speichern - bitte erneut einloggen");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Event wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/events/${id}`, authHeader);
        toast.success("Event gelöscht");
        fetchEvents();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Fehler beim Löschen - bitte erneut einloggen");
      }
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time || "",
      location: event.location || "",
      price: event.price || "",
      image_url: event.image_url || "",
      is_active: event.is_active,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      title: "",
      description: "",
      date: "",
      time: "",
      location: "",
      price: "",
      image_url: "",
      is_active: true,
    });
    setIsDialogOpen(false);
  };

  return (
    <div data-testid="events-manager">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
        <h2 className="font-serif text-2xl md:text-3xl text-yoga-brown">Events verwalten</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => resetForm()}
              className="bg-yoga-brown text-yoga-cream hover:bg-yoga-brown/90 w-full sm:w-auto"
              data-testid="add-event"
            >
              <Plus size={18} className="mr-2" /> Neues Event
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-yoga-cream max-w-[95vw] sm:max-w-lg mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl md:text-2xl text-yoga-brown">
                {editingEvent ? "Event bearbeiten" : "Neues Event"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-yoga-brown">Titel</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1 border-yoga-sand"
                  required
                />
              </div>
              <div>
                <Label className="text-yoga-brown">Beschreibung</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 border-yoga-sand"
                  rows={3}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-yoga-brown">Datum</Label>
                  <Input
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="z.B. 15. März 2026"
                    className="mt-1 border-yoga-sand text-sm"
                    required
                  />
                </div>
                <div>
                  <Label className="text-yoga-brown">Uhrzeit</Label>
                  <Input
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    placeholder="10:00 - 12:00"
                    className="mt-1 border-yoga-sand text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-yoga-brown">Ort (optional)</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="mt-1 border-yoga-sand"
                />
              </div>
              <div>
                <Label className="text-yoga-brown">Preis (optional)</Label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="z.B. 35 €"
                  className="mt-1 border-yoga-sand"
                />
              </div>
              <div>
                <Label className="text-yoga-brown">Event-Bild</Label>
                <div className="mt-2 space-y-3">
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-yoga-sand rounded-lg cursor-pointer hover:border-yoga-brown/50 transition-colors">
                    {isUploading ? (
                      <>
                        <Loader2 size={20} className="animate-spin text-yoga-brown" />
                        <span className="text-yoga-brown text-sm">Wird hochgeladen...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={20} className="text-yoga-brown/60" />
                        <span className="text-yoga-brown/60 text-sm">Bild auswählen</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                  {formData.image_url && (
                    <div className="relative rounded-lg overflow-hidden border border-yoga-sand">
                      <img 
                        src={formData.image_url} 
                        alt="Vorschau" 
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: "" })}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active" className="text-yoga-brown">Aktiv (sichtbar)</Label>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="w-full sm:w-auto">
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-yoga-brown text-yoga-cream hover:bg-yoga-brown/90 w-full sm:w-auto" disabled={isUploading}>
                  {editingEvent ? "Speichern" : "Hinzufügen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {events.map((event) => (
          <div key={event.id} className="bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl p-4">
            <div className="flex gap-3">
              {event.image_url ? (
                <img src={event.image_url} alt={event.title} className="w-16 h-16 object-cover rounded flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 bg-yoga-sand/30 rounded flex items-center justify-center text-yoga-brown/30 text-xs flex-shrink-0">
                  Kein Bild
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-yoga-brown truncate">{event.title}</div>
                <div className="text-sm text-yoga-brown/60">{event.date}</div>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs ${event.is_active ? "bg-yoga-sage/20 text-yoga-brown" : "bg-gray-200 text-gray-600"}`}>
                  {event.is_active ? "Aktiv" : "Inaktiv"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(event)}
                  className="text-yoga-brown hover:bg-yoga-sand/50 p-2"
                >
                  <Pencil size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(event.id)}
                  className="text-red-500 hover:bg-red-50 p-2"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {events.length === 0 && (
          <div className="text-center py-8 text-yoga-brown/50">
            Keine Events vorhanden
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-yoga-sand">
              <TableHead className="text-yoga-brown">Bild</TableHead>
              <TableHead className="text-yoga-brown">Titel</TableHead>
              <TableHead className="text-yoga-brown">Datum</TableHead>
              <TableHead className="text-yoga-brown">Ort</TableHead>
              <TableHead className="text-yoga-brown">Preis</TableHead>
              <TableHead className="text-yoga-brown">Status</TableHead>
              <TableHead className="text-yoga-brown text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id} className="border-yoga-sand">
                <TableCell>
                  {event.image_url ? (
                    <img src={event.image_url} alt={event.title} className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-yoga-sand/30 rounded flex items-center justify-center text-yoga-brown/30 text-xs">
                      Kein Bild
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{event.title}</TableCell>
                <TableCell>{event.date} {event.time && `| ${event.time}`}</TableCell>
                <TableCell>{event.location || "-"}</TableCell>
                <TableCell>{event.price || "-"}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs ${event.is_active ? "bg-yoga-sage/20 text-yoga-brown" : "bg-gray-200 text-gray-600"}`}>
                    {event.is_active ? "Aktiv" : "Inaktiv"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(event)}
                    className="text-yoga-brown hover:bg-yoga-sand/50"
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(event.id)}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-yoga-brown/50">
                  Keine Events vorhanden
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Messages Manager
const MessagesManager = ({ authHeader }) => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`, authHeader);
      setMessages(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Nachrichten");
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/admin/messages/${id}/read`, {}, authHeader);
      fetchMessages();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Nachricht wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/messages/${id}`, authHeader);
        toast.success("Nachricht gelöscht");
        fetchMessages();
        setSelectedMessage(null);
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  return (
    <div data-testid="messages-manager">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="font-serif text-3xl text-yoga-brown">Nachrichten</h2>
          {unreadCount > 0 && (
            <p className="font-sans text-sm text-yoga-sage mt-1">{unreadCount} ungelesen</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <div className="bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl overflow-hidden">
          <div className="divide-y divide-yoga-sand">
            {messages.map((message) => (
              <div
                key={message.id}
                onClick={() => {
                  setSelectedMessage(message);
                  if (!message.is_read) handleMarkRead(message.id);
                }}
                className={`p-4 cursor-pointer hover:bg-yoga-sand/20 transition-colors ${
                  selectedMessage?.id === message.id ? "bg-yoga-sand/30" : ""
                } ${!message.is_read ? "border-l-4 border-yoga-sage" : ""}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-sans font-medium text-yoga-brown">{message.name}</span>
                  <span className="font-sans text-xs text-yoga-brown/50">{formatDate(message.created_at)}</span>
                </div>
                <p className="font-sans text-sm text-yoga-brown/60">{message.email}</p>
                <p className="font-sans text-sm text-yoga-brown/80 mt-1 line-clamp-2">{message.message}</p>
              </div>
            ))}
            {messages.length === 0 && (
              <div className="p-8 text-center text-yoga-brown/50">
                Keine Nachrichten vorhanden
              </div>
            )}
          </div>
        </div>

        {/* Message Detail */}
        {selectedMessage && (
          <div className="bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-serif text-xl text-yoga-brown">{selectedMessage.name}</h3>
                <p className="font-sans text-sm text-yoga-brown/60">{selectedMessage.email}</p>
                {selectedMessage.phone && (
                  <p className="font-sans text-sm text-yoga-brown/60">{selectedMessage.phone}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(selectedMessage.id)}
                className="text-red-500 hover:bg-red-50"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <p className="font-sans text-yoga-brown/80 whitespace-pre-wrap">{selectedMessage.message}</p>
            <div className="mt-6 pt-4 border-t border-yoga-sand">
              <p className="font-sans text-xs text-yoga-brown/40">
                Erhalten am {formatDate(selectedMessage.created_at)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Admin Page
const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authPassword, setAuthPassword] = useState("");

  useEffect(() => {
    const stored = localStorage.getItem("adminAuth");
    if (stored) {
      try {
        const password = atob(stored);
        // Verify the password is still valid
        axios.post(`${API}/admin/login`, { password })
          .then(() => {
            setAuthPassword(password);
            setIsLoggedIn(true);
          })
          .catch(() => {
            // Password no longer valid, clear storage
            localStorage.removeItem("adminAuth");
          });
      } catch {
        localStorage.removeItem("adminAuth");
      }
    }
  }, []);

  const handleLogin = (password) => {
    setAuthPassword(password);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setIsLoggedIn(false);
    setAuthPassword("");
  };

  if (!isLoggedIn) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return <AdminDashboard authPassword={authPassword} onLogout={handleLogout} />;
};

export default AdminPage;
